import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';

import {
  calculateDailyAverage,
  calculateWeeklyAverage,
  generateDateRange,
  generateWeekRange,
} from '../../../../admin/src/utils/functions';
import LineChart from './LineChart';
import CustomTooltip from './CustomTooltip';
import { MarketData } from 'common/src/utils/types';
import { BitacoraPriceData } from 'common/src/api/market_comments';
import ChineseChart from './ChineseChart';
import SeasonWeekChart from './SeasonWeekChart';
import ChineseTooltip from './ChineseTooltip';
import useDataStore from '../../stores/useDataStore';
import { MARKETS, MARKET_LABELS } from '../../utils/definitions';
import useUserStore from '../../stores/useUserStore';
import {
  BitacoraMode,
  appendBitacoraDetails,
  buildBitacoraSeries,
} from './bitacoraSeries';
import { useTranslation } from '../../hooks/useTranslation';
import EvolutionChartFilters from './EvolutionChartFilters';
import ChartDownloadButton from './ChartDownloadButton';

interface MarketsEvolutionChartProps {
  data: MarketData[];
  selectedCurrency: 'RMB' | 'USD';
  rangeSelected: { startDate: string; endDate: string };
  priceView: 'kg' | 'box';
  showPastsSeasons: boolean;
  showExporterPrices: boolean;
  viewMode: 'chinese' | 'normal';
  allMarketData?: MarketData[]; // Optional: processed allMarketData from parent
  visualization?: 'chart' | 'table';
  chartViewMode?: 'day' | 'week';
  bitacoraData?: BitacoraPriceData[];
  bitacoraMode?: BitacoraMode; // Un punto promedio, o uno por cliente o por etiqueta
}

const MarketsEvolutionChart: React.FC<MarketsEvolutionChartProps> =
  ({
    data,
    selectedCurrency,
    rangeSelected,
    priceView,
    showPastsSeasons,
    showExporterPrices,
    viewMode,
    allMarketData: allMarketDataProp,
    visualization = 'chart',
    chartViewMode = 'day',
    bitacoraData = [],
    bitacoraMode = 'average',
  }) => {
    const { t } = useTranslation('market');
    const { allMarketData: allMarketDataStore, selectedSpecies } = useDataStore();
    // Use prop if provided, otherwise fallback to store
    const allMarketData = allMarketDataProp || allMarketDataStore;
    const { userProfile, darkMode } = useUserStore();
    const ownExporter = userProfile?.exporter;

    // Memoize static lists and default values
    // When showing past seasons, build lists from allMarketData to include all varieties/calibers/formats
    const staticData = useMemo(() => {
      const dataSource = showPastsSeasons ? allMarketData : data;

      // Los 3 mercados con más registros (MARKETS, sin 'all')
      const marketCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        const market = item.region?.toUpperCase();
        if (market) {
          marketCounts[market] = (marketCounts[market] || 0) + 1;
        }
      });
      const defaultMarkets = MARKETS.filter((m) => m !== 'all')
        .sort((a, b) => (marketCounts[b] || 0) - (marketCounts[a] || 0))
        .slice(0, 3);

      // Todos los calibres, ordenados por cantidad de registros
      const caliberCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.size) {
          caliberCounts[item.size] = (caliberCounts[item.size] || 0) + 1;
        }
      });
      const defaultCalibers = Object.entries(caliberCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([caliber]) => caliber);

      // Todas las variedades, ordenadas por cantidad de registros
      const varietyCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.variety) {
          varietyCounts[item.variety] = (varietyCounts[item.variety] || 0) + 1;
        }
      });
      const defaultVarieties = Object.entries(varietyCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([variety]) => variety);

      // Todos los formatos, ordenados por cantidad de registros
      const formatCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.weight_kg) {
          formatCounts[item.weight_kg] = (formatCounts[item.weight_kg] || 0) + 1;
        }
      });
      const defaultFormats = Object.entries(formatCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([format]) => format);

      // Mismos códigos que lista ColorFilter (D, L…), en el mismo orden.
      const colorSet = new Set<string>();
      dataSource.forEach((item) => {
        colorSet.add(item.color || 'Sin Color');
      });
      const defaultColors = Array.from(colorSet).sort((a, b) => a.localeCompare(b));

      // Get all fleets (using codes, not translations)
      const fleetSet = new Set<string>();
      dataSource.forEach((item) => {
        if (item.transport === 'AIR') {
          fleetSet.add('AIR');
        } else if (item.transport === 'OCEAN') {
          fleetSet.add('OCEAN');
        }
      });
      const defaultFleets = Array.from(fleetSet);

      return {
        defaultMarkets,
        defaultCalibers,
        defaultVarieties,
        defaultFormats,
        defaultColors,
        defaultFleets,
      };
    }, [data, showPastsSeasons, allMarketData]);

    // States - Initialize with default values (all as arrays for multiple selection)
    const [selectedMarkets, setSelectedMarkets] = useState<string[]>(
      staticData.defaultMarkets
    );
    const [selectedCalibers, setSelectedCalibers] = useState<string[]>(
      staticData.defaultCalibers
    );
    const [selectedVarieties, setSelectedVarieties] = useState<string[]>(
      staticData.defaultVarieties
    );
    const [selectedFormats, setSelectedFormats] = useState<string[]>(
      staticData.defaultFormats
    );
    const [selectedColors, setSelectedColors] = useState<string[]>(
      staticData.defaultColors
    );
    const [selectedFleets, setSelectedFleets] = useState<string[]>(
      staticData.defaultFleets
    );
    const [totalRecordsFiltered, setTotalRecordsFiltered] = useState(0);

    // Track if initial selections have been set
    const initialSelectionsSet = useRef(false);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const previousSpeciesId = useRef(selectedSpecies?.id);

    // Update selections when species changes
    useEffect(() => {
      const speciesChanged = previousSpeciesId.current !== selectedSpecies?.id;

      if (speciesChanged && initialSelectionsSet.current) {
        setSelectedMarkets(staticData.defaultMarkets);
        setSelectedCalibers(staticData.defaultCalibers);
        setSelectedVarieties(staticData.defaultVarieties);
        setSelectedFormats(staticData.defaultFormats);
        setSelectedColors(staticData.defaultColors);
        setSelectedFleets(staticData.defaultFleets);

        previousSpeciesId.current = selectedSpecies?.id;
      } else if (!initialSelectionsSet.current) {
        initialSelectionsSet.current = true;
      }
    }, [
      selectedSpecies?.id,
      staticData.defaultMarkets,
      staticData.defaultCalibers,
      staticData.defaultVarieties,
      staticData.defaultFormats,
      staticData.defaultColors,
      staticData.defaultFleets,
    ]);

    // Memoize filter function - All filters are now arrays (multiple selection)
    const filterData = useCallback(
      (marketData: MarketData[]) => {
        return marketData.filter((item) => {
          // Market filter (note: we don't filter by region here - let prepareChartData handle it)
          // This is intentional as the market lines are defined by selectedMarkets

          // Caliber filter
          if (selectedCalibers.length > 0 && !selectedCalibers.includes(item.size)) {
            return false;
          }

          // Variety filter
          if (selectedVarieties.length > 0 && !selectedVarieties.includes(item.variety)) {
            return false;
          }

          // Format filter
          if (selectedFormats.length > 0 && !selectedFormats.includes(item.weight_kg)) {
            return false;
          }

          // Color filter
          if (selectedColors.length > 0) {
            const itemColor = item.color || 'Sin Color';
            if (!selectedColors.includes(itemColor)) {
              return false;
            }
          }

          // Fleet filter
          if (selectedFleets.length > 0) {
            if (!selectedFleets.includes(item.transport)) {
              return false;
            }
          }

          return true;
        });
      },
      [selectedCalibers, selectedVarieties, selectedFormats, selectedColors, selectedFleets]
    );

    // Chart data preparation
    const prepareChartData = useCallback(
      (
        marketData: MarketData[],
        dateRange?: { startDate: string; endDate: string },
        viewMode: 'day' | 'week' = 'day'
      ) => {
        const filteredData = filterData(marketData);
        let totalRecords = 0;

        // Get the full date/week range for alignment
        const allDates = dateRange
          ? viewMode === 'day'
            ? generateDateRange(dateRange.startDate, dateRange.endDate)
            : generateWeekRange(dateRange.startDate, dateRange.endDate)
          : [];

        // Regular series
        const regularSeries = selectedMarkets.map((market, index) => {
          const marketData = filteredData.filter(
            (item) => item?.region?.toLowerCase() === market?.toLowerCase()
          );
          totalRecords += marketData.length;
          const dailyAverages =
            viewMode === 'day'
              ? calculateDailyAverage(marketData)
              : calculateWeeklyAverage(marketData);

          // Create a map of date/week to value
          const dataMap = new Map<string, { y: number; count: number }>();
          dailyAverages.forEach((avg) => {
            if (viewMode === 'day') {
              const date = new Date(avg.x);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              const dateStr = `${year}-${month}-${day}`;
              dataMap.set(dateStr, { y: avg.y, count: (avg as any).count || 1 });
            } else {
              // For weekly view, avg.x is already "W{number}"
              dataMap.set(String(avg.x), { y: avg.y, count: (avg as any).count || 1 });
            }
          });

          // Map to full date range
          let alignedData: { x: string; y: number | null; count?: number }[];
          if (allDates.length > 0) {
            alignedData = allDates.map(dateStr => {
              const point = dataMap.get(dateStr);
              return {
                x: dateStr,
                y: point?.y ?? null,
                count: point?.count
              };
            });
          } else if (dataMap.size > 0) {
            alignedData = Array.from(dataMap).map(([dateStr, point]) => ({
              x: dateStr,
              y: point.y,
              count: point.count
            }));
          } else {
            alignedData = [];
          }

          return {
            name: MARKET_LABELS[market],
            data: alignedData,
            color: `hsl(${index * (360 / selectedMarkets.length)}, 70%, 50%)`,
            type: 'line',
          };
        });

        // Exporter series
        const exporterSeries = showExporterPrices
          ? selectedMarkets.map((market, index) => {
            const marketData = filteredData.filter(
              (item) =>
                item?.region?.toLowerCase() === market?.toLowerCase() &&
                item.exporter === ownExporter?.excel_name
            );
            totalRecords += marketData.length;

            const exporterDailyAverages =
              viewMode === 'day'
                ? calculateDailyAverage(marketData)
                : calculateWeeklyAverage(marketData);

            // Create a map of date/week to value
            const exporterDataMap = new Map<string, { y: number; count: number }>();
            exporterDailyAverages.forEach((avg) => {
              if (viewMode === 'day') {
                const date = new Date(avg.x);
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                exporterDataMap.set(dateStr, { y: avg.y, count: (avg as any).count || 1 });
              } else {
                // For weekly view, avg.x is already "W{number}"
                exporterDataMap.set(String(avg.x), { y: avg.y, count: (avg as any).count || 1 });
              }
            });

            // Map to full date range
            let alignedData: { x: string; y: number | null; count?: number }[];
            if (allDates.length > 0) {
              alignedData = allDates.map(dateStr => {
                const point = exporterDataMap.get(dateStr);
                return {
                  x: dateStr,
                  y: point?.y ?? null,
                  count: point?.count
                };
              });
            } else if (exporterDataMap.size > 0) {
              alignedData = Array.from(exporterDataMap).map(([dateStr, point]) => ({
                x: dateStr,
                y: point.y,
                count: point.count
              }));
            } else {
              alignedData = [];
            }

            return {
              name: `${MARKET_LABELS[market]} ${ownExporter?.excel_name || 'Exportadora'
                }`,
              data: alignedData,
              color: `hsl(${index * (360 / selectedMarkets.length)}, 70%, ${showPastsSeasons ? 30 : 80
                }%)`,
              isExporter: true,
              type: 'line',
              markers: {
                size: 6,
                shape: 'diamond',
                strokeWidth: 2,
                hover: {
                  size: 8,
                },
              },
            };
          })
          : [];

        setTotalRecordsFiltered(totalRecords);

        // Puntos de Bitácora: promedio ponderado por bultos, o uno por cliente o por etiqueta.
        // En este gráfico entran todos los registros, sin filtrar.
        const bitacoraSeries = buildBitacoraSeries({
          records: allDates.length > 0 ? bitacoraData : [],
          categories: allDates,
          categoryOf: (b) => b.date_from,
          mode: bitacoraMode,
          label: t('Bitácora'),
          noClientLabel: t('Sin cliente'),
          noLabelLabel: t('Sin etiqueta o varias'),
        }).map((series) => ({ ...series, isBitacora: true }));

        return {
          series: [...regularSeries, ...exporterSeries, ...bitacoraSeries],
          categories: Array.from(
            new Set(filteredData.map((item) => item.date))
          ).sort(),
        };
      },
      [
        filterData,
        selectedMarkets,
        ownExporter?.excel_name,
        showExporterPrices,
        showPastsSeasons,
        bitacoraData,
        bitacoraMode,
      ]
    );

    const chartData = useMemo(
      () => {
        return prepareChartData(
          showPastsSeasons ? allMarketData : data,
          !showPastsSeasons ? rangeSelected : undefined,
          chartViewMode
        );
      },
      [
        prepareChartData,
        showPastsSeasons,
        allMarketData,
        data,
        rangeSelected,
        chartViewMode,
      ]
    );
    const tooltipCustom = ({
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }): string => {
      // Obtenemos la fecha del punto actual (ahora es un string)
      const currentSeries = w.globals.initialSeries[seriesIndex];
      const currentPoint = currentSeries.data[dataPointIndex];

      if (!currentPoint) return '';

      // Tooltip compartido: al final se agrega el detalle de los registros de
      // Bitácora de esa fecha (cliente, comentario, foto, quién lo registró).
      const withBitacora = (html: string) =>
        appendBitacoraDetails(html, {
          w,
          x: currentPoint.x,
          currency: selectedCurrency,
          unit: priceView === 'kg' ? '/kg' : '/5kg box',
          darkMode,
          t,
        });



      if (viewMode === 'chinese') {
        return withBitacora(ChineseTooltip({
          seriesIndex,
          dataPointIndex,
          w,
          selectedCurrency,
          priceView,
        }) as string);
      }

      return withBitacora(CustomTooltip({
        seriesIndex,
        dataPointIndex,
        w,
        selectedCurrency,
        chartType: 'market',
        priceView: priceView,
      }) as string);
    };

    // Table View Component
    const TableView = () => {
      // Prepare table data - dates as columns, series as rows
      const tableData = useMemo(() => {
        // Get all unique dates sorted
        const allDates = new Set<string>();
        chartData.series.forEach((serie) => {
          serie.data.forEach((point) => {
            allDates.add(point.x);
          });
        });
        const sortedDates = Array.from(allDates).sort();

        // Build data structure: each row is a series, each column is a date
        const rows = chartData.series.map((serie) => {
          const dateMap = new Map(
            serie.data.map((point) => [point.x, point.y])
          );
          return {
            serieName: serie.name,
            values: sortedDates.map((date) => dateMap.get(date) ?? null),
          };
        });

        return { dates: sortedDates, rows };
      }, [chartData.series]);

      return (
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-default-200'>
            <thead className='bg-default-100 sticky top-0'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-default-500 uppercase tracking-wider sticky left-0 bg-default-100 z-10'>
                  {t('Serie')}
                </th>
                {tableData.dates.map((dateStr) => (
                  <th
                    key={dateStr}
                    className='px-6 py-3 text-left text-xs font-medium text-default-500 uppercase tracking-wider'
                  >
                    {new Date(dateStr).toLocaleDateString()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='bg-background divide-y divide-default-200'>
              {tableData.rows.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-background' : 'bg-default-100'}>
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-default-900 sticky left-0 bg-inherit'>
                    {row.serieName}
                  </td>
                  {row.values.map((value, dateIdx) => (
                    <td
                      key={dateIdx}
                      className='px-6 py-4 whitespace-nowrap text-sm text-default-500'
                    >
                      {value != null
                        ? `${selectedCurrency} ${value.toFixed(2)}`
                        : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div>
        <div className='flex justify-between items-center mb-4'>
          <div className='flex gap-4 flex-col w-full'>
            <div className='flex flex-col sm:flex-row justify-between items-center'>
              <div className='flex items-center gap-2'>
                <h3 className='text-lg font-bold'>{t('Por mercado')}</h3>
                {visualization !== 'table' && (
                  <ChartDownloadButton
                    chartRef={chartContainerRef}
                    title={t('Por mercado')}
                    subtitle={`${totalRecordsFiltered} ${t('registros totales')}`}
                  />
                )}
              </div>
              <p className='text-sm text-default-500'>
                {totalRecordsFiltered} {t('registros totales')}
              </p>
            </div>
            <EvolutionChartFilters
              primaryFilter="market"
              data={showPastsSeasons ? allMarketData : data}
              selectedCalibers={selectedCalibers}
              onCalibersChange={setSelectedCalibers}
              selectedVarieties={selectedVarieties}
              onVarietiesChange={(values) => setSelectedVarieties(values)}
              selectedFormats={selectedFormats}
              onFormatsChange={setSelectedFormats}
              selectedMarkets={selectedMarkets}
              onMarketsChange={setSelectedMarkets}
              selectedColors={selectedColors}
              onColorsChange={setSelectedColors}
              selectedFleets={selectedFleets}
              onFleetsChange={setSelectedFleets}
              hasColor={selectedSpecies?.has_color || false}
            />
          </div>
        </div>
        {visualization === 'table' ? (
          <TableView />
        ) : (
          <div ref={chartContainerRef}>
            {viewMode === 'chinese' ? (
              <ChineseChart
                series={chartData.series}
                xaxisCategories={chartData.categories.map((c) => String(c))}
                priceView={priceView}
                height={350}
                yaxisTitle={`Precio ${selectedCurrency}/${priceView === 'kg' ? 'Kg' : '5kg box'
                  }`}
                tooltipCustom={tooltipCustom}
                selectedCurrency={selectedCurrency}
              />
            ) : showPastsSeasons ? (
              <SeasonWeekChart
                series={chartData.series}
                xaxisCategories={chartData.categories.map((c) => String(c))}
                priceView={priceView}
                height={350}
                yaxisTitle={`Precio ${selectedCurrency}/${priceView === 'kg' ? 'Kg' : '5kg box'
                  }`}
                tooltipCustom={tooltipCustom}
                selectedCurrency={selectedCurrency}
              />
            ) : (
              <LineChart
                series={chartData.series}
                xaxisCategories={
                  chartViewMode === 'day'
                    ? generateDateRange(
                      rangeSelected.startDate,
                      rangeSelected.endDate
                    )
                    : generateWeekRange(
                      rangeSelected.startDate,
                      rangeSelected.endDate
                    )
                }
                yaxisTitle={`Precio ${selectedCurrency} / ${priceView === 'kg' ? 'Kg' : '5kg box'
                  }`}
                tooltipCustom={tooltipCustom}
                selectedCurrency={selectedCurrency}
                priceView={priceView}
              />
            )}
          </div>
        )}
      </div>
    );
  };

export default memo(MarketsEvolutionChart);
